// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
// Utils
import { getRequest as get, deleteRequest } from '../utils/HttpMethods';
import queryString from '../utils/queryStringBuilder';
import {
  createEntity,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
  usePaginatedQuery,
} from '../utils/queries';
import {
  PaginatedResult,
  PaginationParams,
} from '../utils/queries/usePaginatedListQuery';
import {
  convertFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';

// Interfaces
import { Report, isReport } from 'interfaces';

export const reportQueryKeys = {
  all: ['reports'],
  paginatedReportsBySite: (
    siteID: string,
    page: number,
    pageSize: number,
    filters?: PaginationParams['filters'],
    globalFilter?: PaginationParams['globalFilter'],
    sorting?: PaginationParams['sorting']
  ) => [
    ...reportQueryKeys.all,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  paginatedReportsByUser: (
    userID: string,
    siteID: string | string[] | undefined,
    page: number,
    pageSize: number,
    filters?: PaginationParams['filters'],
    globalFilter?: PaginationParams['globalFilter'],
    sorting?: PaginationParams['sorting'],
    type?: string
  ) => [
    ...reportQueryKeys.all,
    userID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
    type,
  ],
  listReportsByProvider: (providerID: string) => [
    ...reportQueryKeys.all,
    providerID,
  ],
  get: (reportID: string) => [...reportQueryKeys.all, reportID],
};

/**
 * @description Creates a new report.
 * @param {Report} report - The report object to create.
 * @returns {Promise<Report>} A promise that resolves to the created Report object.
 */
const createReport = async (report: Report): Promise<Report> => {
  return createEntity<Report>({
    entity: report,
    functionName: 'createReport',
    isEntity: isReport,
    path: '/resource/report',
  });
};

/**
 * @description Hook to create a new report.
 * @returns {UseMutationResult<Report, Error>} The result of the mutation.
 */
export const useCreateReport = (): UseMutationResult<Report, Error> => {
  return useMutationQuery<Report>({
    mutationFn: createReport,
    errorMessage: 'Failed to create Report',
    successMessage: 'Report Created',
    queryKey: reportQueryKeys.all,
  });
};

/**
 * @description Updates an existing report.
 * @param {Report} report - The report object to update.
 * @returns {Promise<Report>} A promise that resolves to the updated Report object.
 */
const updateReport = async (report: Report): Promise<Report> => {
  return updateEntity<Report>({
    entity: report,
    functionName: 'updateReport',
    isEntity: isReport,
    path: '/resource/report',
  });
};

/**
 * @description Hook to update an existing report.
 * @returns {UseMutationResult<Report, Error>} The result of the mutation.
 */
export const useUpdateReport = (): UseMutationResult<Report, Error> => {
  return useMutationQuery<Report>({
    mutationFn: updateReport,
    errorMessage: 'Failed to update Report',
    successMessage: 'Report Updated',
    queryKey: reportQueryKeys.all,
  });
};

/**
 * @description Fetches a list of all reports.
 * @returns {Promise<Report[]>} A promise that resolves to an array of Report objects.
 */
const listReports = async (): Promise<Report[]> =>
  await get<Report[]>('QBilling', `/resource/report/all`);

/**
 * @description Hook to fetch a list of all reports.
 * @param {Object} params - Parameters for the hook.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Report[], Error>} The result of the query.
 */
export const useListReports = ({
  displayNotification = false,
}: {
  displayNotification?: boolean;
}): UseQueryResult<Report[], Error> => {
  return useListQuery<Report>({
    queryKey: reportQueryKeys.all,
    fetchFunction: listReports,
    functionName: 'listReports',
    isEntity: isReport,
    enabled: true,
    displayNotification,
  });
};

/**
 * @description Fetches a report by its ID.
 * @param {string} reportID - The ID of the report.
 * @returns {Promise<Report | null>} A promise that resolves to a Report object or null.
 */
export const getReportByID = async (reportID: string): Promise<Report | null> =>
  await get<Report>(
    'QBilling',
    `/resource/report${queryString({ id: reportID })}`
  );

/**
 * @description Hook to fetch a report by its ID.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.reportID - The ID of the report.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Report | null, Error>} The result of the query.
 */
export const useGetReport = ({
  reportID,
  displayNotification = false,
}: {
  displayNotification?: boolean;
  reportID: string;
}): UseQueryResult<Report | null, Error> => {
  return useGetQuery<Report>({
    queryKey: reportQueryKeys.get(reportID),
    fetchFunction: getReportByID,
    functionName: 'getReport',
    params: reportID,
    isEntity: isReport,
    enabled: !!reportID,
    displayNotification,
  });
};

/**
 * @description Fetches a list of reports by site ID.
 * @param {string} siteID - The ID of the site.
 * @returns {Promise<Report[]>} A promise that resolves to an array of Report objects.
 */
const paginatedReportsBySite = async ({
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<PaginatedResult<Report>> => {
  const filterString = convertFiltersToQueryString(filters ?? []);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting ?? []);
  return await get<PaginatedResult<Report>>(
    'QBilling',
    `/resource/report/site${queryString({
      siteID,
      page,
      pageSize,
      filters: filterString,
      globalFilter: globalFilterString,
      sorting: sortingString,
    })}`
  );
};

/**
 * @description Hook to fetch a list of reports by site ID.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.siteID - The ID of the site.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Report[], Error>} The result of the query.
 */
export const useListReportsBySite = ({
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
  displayNotification = false,
}: {
  siteID: string;
  page: number;
  pageSize: number;
  filters?: PaginationParams['filters'];
  globalFilter?: PaginationParams['globalFilter'];
  sorting?: PaginationParams['sorting'];
  displayNotification?: boolean;
}): UseQueryResult<PaginatedResult<Report>, Error> => {
  return usePaginatedQuery<Report>({
    queryKey: reportQueryKeys.paginatedReportsBySite(
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting
    ),
    fetchFunction: paginatedReportsBySite,
    functionName: 'listReportsBySite',
    params: { siteID, page, pageSize, filters, globalFilter, sorting },
    isEntity: isReport,
    enabled: !!siteID,
    displayNotification,
  });
};

/**
 * @description Fetches a list of reports by user ID and type.
 * @param {Object} params - Parameters for the function.
 * @param {string} params.userID - The ID of the user.
 * @param {string} params.type - The type of the report.
 * @returns {Promise<Report[]>} A promise that resolves to an array of Report objects.
 */
const paginatedReportsByUser = async ({
  userID,
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
  type,
}: PaginationParams & { type?: string }): Promise<PaginatedResult<Report>> => {
  const filterString = convertFiltersToQueryString(filters ?? []);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting ?? []);
  return await get<PaginatedResult<Report>>(
    'QBilling',
    `/resource/report/user${queryString({
      userID,
      siteID,
      page,
      pageSize,
      filters: filterString,
      globalFilter: globalFilterString,
      sorting: sortingString,
      type,
    })}`
  );
};

/**
 * @description Hook to fetch a list of reports by user ID and type.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.userID - The ID of the user.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @param {string} params.type - The type of the report.
 * @returns {UseQueryResult<Report[], Error>} The result of the query.
 */
export const useListReportsByUser = ({
  userID,
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
  displayNotification = false,
  type,
  enabled = true,
}: {
  userID: string;
  siteID?: string | string[];
  page: number;
  pageSize: number;
  filters?: PaginationParams['filters'];
  globalFilter?: PaginationParams['globalFilter'];
  sorting?: PaginationParams['sorting'];
  displayNotification?: boolean;
  type?: string;
  enabled?: boolean;
}): UseQueryResult<PaginatedResult<Report>, Error> => {
  return usePaginatedQuery<Report>({
    queryKey: reportQueryKeys.paginatedReportsByUser(
      userID,
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
      type
    ),
    fetchFunction: (params) => paginatedReportsByUser({ ...params, type }),
    functionName: 'listReportsByUser',
    params: { userID, siteID, page, pageSize, filters, globalFilter, sorting },
    isEntity: isReport,
    enabled: !!userID && enabled,
    displayNotification,
  });
};

const listReportsByProvider = async (providerID: string): Promise<Report[]> =>
  await get<Report[]>(
    'QBilling',
    `/resource/report/provider${queryString({ providerID })}`
  );

export const useListReportsByProvider = ({
  providerID,
  displayNotification = false,
}: {
  providerID: string;
  displayNotification?: boolean;
}): UseQueryResult<Report[], Error> => {
  return useListQuery<Report>({
    queryKey: reportQueryKeys.listReportsByProvider(providerID),
    fetchFunction: listReportsByProvider,
    functionName: 'listReportsByProvider',
    params: providerID,
    isEntity: isReport,
    enabled: !!providerID,
    displayNotification,
  });
};

const deleteReport = async (reportID: string): Promise<void> =>
  await deleteRequest(
    'QBilling',
    `/resource/report/${queryString({ reportID })}`
  );

export const useDeleteReport = (): UseMutationResult<void, Error> => {
  return useMutationQuery<void>({
    mutationFn: deleteReport,
    errorMessage: 'Failed to delete Report',
    successMessage: 'Report Deleted',
    queryKey: reportQueryKeys.all,
  });
};
